import React, { useEffect, useState } from "react";
import { ImageBackground, StyleSheet, View, Image, PixelRatio } from "react-native";
import { footWidthLeft, footLengthRight, footWidthRight, footLengthLeft } from "../../assets";
import { background } from "../../assets";
import ThreeStage from "../../components/3DScene/ThreeStage";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import { useGlobalState } from "../../shared/state";
import { Styles } from "../../styles";
import MeasurementsPanel from "./MeasurementsPanel";
import MeasurementsTabs from "./MeasurementsTabs";
import { I18N } from "../../shared/util";
import TextTitle from "../../components/Text/TextTitle";
import SquareButton from "../../components/Buttons/generic-button/SquareButton";
import I18n from "../../i18nSets/i18n";

type BasicMeasurements = {
  left: {
    length: number | undefined;
    width: number | undefined;
    widthHeel: number | undefined;
    heightInstepPoint: number | undefined;
    widthCategory: number | undefined;
  };
  right: {
    length: number | undefined;
    width: number | undefined;
    widthHeel: number | undefined;
    heightInstepPoint: number | undefined;
    widthCategory: number | undefined;
  };
};

const mockTabs = [
  {
    tabId: "measurements",
    caption: "measurements",
  },
  {
    tabId: "analysis",
    caption: "analysis",
  },
  {
    tabId: "more",
    caption: "more",
  },
];

const Measurements = () => {
  const [currentMeasurements] = useGlobalState("currentMeasurements");
  const [currentScan] = useGlobalState("currentScan");
  const [currentScanner] = useGlobalState("currentScanner");
  const [currentUser] = useGlobalState("currentUser");
  const [language] = useGlobalState("language");

  const [tabId, setTabId] = useState("measurements");
  const [meshURLS, setMeshURLS] = useState<any>(undefined);
  const [meshSole, setMeshSole] = useState<any>(undefined);
  const [basicMeasurement, setBasicMeasurement] = useState<BasicMeasurements>({
    left: { length: undefined, width: undefined, widthCategory: undefined },
    right: { length: undefined, width: undefined, widthCategory: undefined },
  });

  const isBirkenstock = currentScanner && currentScanner.partner && currentScanner.partner?.toLowerCase() === "birkenstock";

  const i18n = I18N(language);

  const switchTab = (tabId: string) => {
    setTabId(tabId);
  };

  // convert MM TO CM
  const decimalAdjuster = (floatNumber: number) => parseFloat(((floatNumber * 10) / 100).toFixed(1));

  useEffect(() => {
    if (currentScan && !meshURLS) {
      setMeshURLS({
        left: currentScan.scanfiles.find((result: any) => result.side === "left").mesh_url,
        right: currentScan.scanfiles.find((result: any) => result.side === "right").mesh_url,
      });
      setMeshSole({
        left: currentScan.scanfiles.find((result: any) => result.side === "left").colored_sole,
        right: currentScan.scanfiles.find((result: any) => result.side === "right").colored_sole,
      });
    }
  }, []);

  // get basic measurements from current Measurements
  useEffect(() => {
    if (currentMeasurements) {
      Object.keys(currentMeasurements.measurements).map((measure: any) => {
        if (currentMeasurements.measurements[measure].side === "Left") {
          setMeshSole((prevState: any) => ({
            ...prevState,
            left: currentMeasurements.measurements[measure].colored_sole,
          }));

          setBasicMeasurement((prevState: any) => ({
            ...prevState,
            left: {
              length: decimalAdjuster(currentMeasurements.measurements[measure].measurements.length_foot),
              width: decimalAdjuster(currentMeasurements.measurements[measure].measurements.width_ball),
              widthHeel: decimalAdjuster(currentMeasurements.measurements[measure].measurements.width_heel),
              heightInstepPoint: decimalAdjuster(currentMeasurements.measurements[measure].measurements.height_instep_point),
              widthCategory: currentMeasurements.measurements[measure].measurements.width_category,
            },
          }));
        } else {
          setMeshSole((prevState: any) => ({
            ...prevState,
            right: currentMeasurements.measurements[measure].colored_sole,
          }));
          setBasicMeasurement((prevState: any) => ({
            ...prevState,
            right: {
              length: decimalAdjuster(currentMeasurements.measurements[measure].measurements.length_foot),
              width: decimalAdjuster(currentMeasurements.measurements[measure].measurements.width_ball),
              widthHeel: decimalAdjuster(currentMeasurements.measurements[measure].measurements.width_heel),
              heightInstepPoint: decimalAdjuster(currentMeasurements.measurements[measure].measurements.height_instep_point),
              widthCategory: currentMeasurements.measurements[measure].measurements.width_category,
            },
          }));
        }
      });
    }
  }, [currentMeasurements]);

  return (
    <ImageBackground source={!isBirkenstock && background} style={Styles.backgroundImage} blurRadius={15}>
      <LayoutWrapper
        showHeaderCloseButton={false}
        showHeaderLogo={false}
        hideInfoButton={isBirkenstock && true}
        hideBackButton={isBirkenstock && true}
        showBirkenstockFooterLogo={isBirkenstock && true}
        showBirkenstockFooter={isBirkenstock && true}
      >
        <View style={[styles.container, isBirkenstock && styles.birkenstockContainer]}>
          {isBirkenstock ? (
            <View style={styles.sidebar}>
              <TextTitle text={i18n.t("measurements").toLocaleUpperCase()} fontSize={40} fontFamily="Birkentype" color="#0e2288" />

              <View style={styles.card}>
                {/* LENGTH */}
                <View style={styles.section}>
                  <View style={styles.sectionHeader}>
                    <TextTitle text={i18n.t("MeasurementScale.length")} fontSize={26} color="#58595bff" fontFamily="Birkentype" />
                  </View>

                  <View style={styles.row}>
                    <Image source={footLengthLeft} style={styles.image} />

                    <View style={styles.valueColumn}>
                      <TextTitle text={i18n.t("MeasurementScale.left")} fontSize={24} color="#58595bff" fontFamily="Geist-Light" />
                      <TextTitle text={`${basicMeasurement.left.length?.toString()} cm` || "0"} fontSize={21} color="#58595bff" fontFamily="Geist-Light" />
                    </View>

                    <Image source={footLengthRight} style={styles.image} />

                    <View style={styles.valueColumn}>
                      <TextTitle text={i18n.t("MeasurementScale.right")} fontSize={24} color="#58595bff" fontFamily="Geist-Light" />
                      <TextTitle text={`${basicMeasurement.right.length?.toString()} cm` || "0"} fontSize={21} color="#58595bff" fontFamily="Geist-Light" />
                    </View>
                  </View>
                </View>

                <View style={styles.divider} />

                {/* WIDTH */}
                <View style={styles.section}>
                  <View style={styles.sectionHeader}>
                    <TextTitle text={i18n.t("MeasurementScale.width")} fontSize={26} color="#58595bff" fontFamily="Birkentype" />
                  </View>

                  <View style={styles.row}>
                    <Image source={footWidthLeft} style={styles.image} />

                    <View style={styles.valueColumn}>
                      <TextTitle text={i18n.t("MeasurementScale.left")} fontSize={24} color="#58595bff" fontFamily="Geist-Light" />
                      <TextTitle text={`${basicMeasurement.left.width?.toString()} cm` || "0"} fontSize={21} color="#58595bff" fontFamily="Geist-Light" />
                    </View>

                    <Image source={footWidthRight} style={styles.image} />

                    <View style={styles.valueColumn}>
                      <TextTitle text={i18n.t("MeasurementScale.right")} fontSize={24} color="#58595bff" fontFamily="Geist-Light" />
                      <TextTitle text={`${basicMeasurement.right.width?.toString()} cm` || "0"} fontSize={21} color="#58595bff" />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          ) : (
            <View style={styles.measurementsPanelContainer}>
              <View style={styles.measurementsTabs}>
                <MeasurementsTabs tabs={mockTabs} activeTab={tabId} onClickHandler={switchTab} />
              </View>
              <View style={styles.measurementsPanel}>
                <MeasurementsPanel panelId={tabId} BasicMeasurements={basicMeasurement} />
              </View>
            </View>
          )}

          <View style={[styles.sceneContainer, isBirkenstock && styles.sceneContainerBirkenstock]}>
            {meshSole ? (
              currentScan && meshURLS ? (
                <ThreeStage leftSole={meshSole.left} leftPLY={meshURLS.left} rightPLY={meshURLS.right} rightSole={meshSole.right} viewMode={isBirkenstock && "top"} maxDistance={isBirkenstock && 750} />
              ) : (
                <ThreeStage leftSole={meshSole.left} rightSole={meshSole.right} email={currentUser ? currentUser.email || currentUser.employeeNumber : undefined} viewMode={isBirkenstock && "top"} maxDistance={isBirkenstock && 750} />
              )
            ) : null}
          </View>

          {isBirkenstock && (
            <View style={styles.footer}>
              <SquareButton
                text={I18n.t("next", { locale: language })}
                color="#0e2288"
                navigateTo="BirkenstockSizeRecommendation"
                navigateOptions={{ resetState: true }}
                deactivated={currentScanner === undefined}
                width={337}
                height={77}
                textColor="white"
                fontSize={36}
              />
            </View>
          )}
        </View>
      </LayoutWrapper>
    </ImageBackground>
  );
};

export default Measurements;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
  },

  birkenstockContainer: {
    justifyContent: "space-evenly",
    maxHeight: "100%",
    flexWrap: "wrap",
  },

  sidebar: {
    width: "25%",
    height: "80%",
    alignSelf: "center",
    justifyContent: "space-between",
  },

  card: {
    width: "95%",
    height: "90%",
    marginTop: "2%",
    justifyContent: "space-around",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, .71)",
    shadowColor: "#000",
    shadowOffset: { width: 6 / PixelRatio.get(), height: 6 / PixelRatio.get() },
    shadowOpacity: 0.16,
    shadowRadius: 4 / PixelRatio.get(),
    elevation: 1,
  },

  section: {
    width: "100%",
    height: "45%",
    flexDirection: "column",
    justifyContent: "space-around",
    alignItems: "center",
  },

  sectionHeader: {
    width: "90%",
    height: "15%",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingLeft: "5%",
    paddingTop: "2.5%",
  },

  row: {
    width: "85%",
    height: "80%",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
  },

  valueColumn: {
    width: "35%",
    height: "50%",
    flexDirection: "column",
    justifyContent: "space-around",
    alignItems: "center",
  },

  image: {
    width: "10%",
    height: "75%",
    resizeMode: "contain",
  },

  divider: {
    width: "85%",
    height: 1,
    backgroundColor: "#ccc",
  },

  footer: {
    width: "100%",
    minHeight: "13%",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "0.5%",
  },

  sceneContainer: {
    flex: 1,
  },

  sceneContainerBirkenstock: {
    maxWidth: "55%",
  },

  measurementsTabs: {
    height: "12%",
    marginLeft: "-3%",
  },

  measurementsPanelContainer: {
    width: "25%",
    height: "95%",
    alignSelf: "flex-end",
  },

  measurementsPanel: {
    height: "88%",
    marginLeft: "-3%",
    backgroundColor: "rgba(255, 255, 255, .71)",
  },
});
